import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import clsx from 'classnames';
import {
  ArrowLeft,
  ArrowRight,
  BadgePercent,
  BarChart3,
  Check,
  ChevronDown,
  Flame,
  Headphones,
  Minus,
  PhoneCall,
  ShoppingBag,
  Users,
  Wallet,
} from 'lucide-react';

import planStyles from '../Payments/containers/SubscriptionPlans/SubscriptionPlans.module.scss';
import styles from './PremiumUpgrade.module.scss';
import { settingsActions } from '../../store';
import {
  getCustomerBill,
  getCustomerInvoices,
  getCustomerPlan,
  getCustomerProducts,
  getPlansLoading,
  getProductsLoading,
  getSubscriptionError,
  getSubscriptionStatus,
  getSubscriptionSubmitting,
} from '../../store/selectors';
import { getAuth } from 'Containers/Auth/store/selectors';
import {
  buildIntervalOptions,
  buildTierComparisonRows,
  filterPlansByTier,
  findPlanByPriceId,
  formatEuroAmount,
  isCustomPlanMode,
  parsePlanDisplay,
  resolvePlanCatalogIds,
  resolvePremiumPriceIds,
  resolveSelectableIntervalType,
  sortPlansByInterval,
} from '../Payments/utils/subscriptionUtils';
import {
  isSepaProcessing,
  isSubscriptionLocked,
  isPaymentFailed,
  getInvoicePaymentStatus,
  getPayableInvoice,
  getRelevantOpenInvoice,
  invoiceHasPendingPayment,
  PAYMENT_METHOD,
  getPaymentMethodType,
} from 'utils/subscriptionGate';
import useIsPremiumUnlocked from 'utils/useIsPremiumUnlocked';
import { PaymentsApi } from 'entries/apis/Payments.api';
import { ROUTES } from 'routes/constants';

const paymentsApi = PaymentsApi.getInstance();

const FEATURES = [
  { key: 'staff', Icon: Users },
  { key: 'stats', Icon: BarChart3 },
  { key: 'profits', Icon: Wallet },
  { key: 'shop', Icon: ShoppingBag },
  { key: 'phone', Icon: PhoneCall },
  { key: 'fees', Icon: BadgePercent },
];

const FAQ_KEYS = ['activation', 'cancel', 'proration', 'data'];

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const PremiumUpgrade = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(getAuth());
  const products = useSelector(getCustomerProducts());
  const plansData = useSelector(getCustomerPlan());
  const subscriptionStatus = useSelector(getSubscriptionStatus());
  const invoices = useSelector(getCustomerInvoices());
  const billInformation = useSelector(getCustomerBill());
  const productsLoading = useSelector(getProductsLoading());
  const plansLoading = useSelector(getPlansLoading());
  const isSubmitting = useSelector(getSubscriptionSubmitting());
  const subscriptionError = useSelector(getSubscriptionError());
  const isAlreadyPremium = useIsPremiumUnlocked();

  const [confirmPlan, setConfirmPlan] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [selectedIntervalType, setSelectedIntervalType] = useState(null);

  const plans = plansData?.plans || [];
  const stripeClientId = products?.idCustomerStripe || user?.idCostumerStripe;
  const isCustomPlan = isCustomPlanMode(products?.planMode);
  const currentPriceId = subscriptionStatus?.currentPriceId;

  const openInvoice = useMemo(() => getRelevantOpenInvoice(invoices), [invoices]);
  const payableInvoice = useMemo(() => getPayableInvoice(invoices), [invoices]);
  const unpaidInvoiceUrl = payableInvoice?.hosted_invoice_url || null;
  const sepaProcessing =
    isSepaProcessing(products, billInformation) ||
    (getPaymentMethodType(products, billInformation) === PAYMENT_METHOD.SEPA &&
      invoiceHasPendingPayment(openInvoice, invoices));
  const locked = isSubscriptionLocked(products, billInformation);
  const sepaFailed =
    !!openInvoice &&
    getPaymentMethodType(products, billInformation) === PAYMENT_METHOD.SEPA &&
    isPaymentFailed(getInvoicePaymentStatus(openInvoice));

  useEffect(() => {
    dispatch(settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST({}, null, { skipLoader: true }));
    dispatch(settingsActions.GET_SUBSCRIPTION_STATUS.REQUEST({}, null, { skipLoader: true }));
  }, [dispatch]);

  useEffect(() => {
    if (stripeClientId) {
      dispatch(
        settingsActions.GET_CUSTOMER_INVOICE.REQUEST(
          { clientId: stripeClientId },
          null,
          { skipLoader: true },
        ),
      );
      dispatch(
        settingsActions.GET_CUSTOMER_BILL.REQUEST(
          { clientId: stripeClientId },
          null,
          { skipLoader: true },
        ),
      );
    }
  }, [dispatch, stripeClientId]);

  useEffect(() => {
    if (!products) return;
    const productIds = resolvePlanCatalogIds(products, [currentPriceId]);
    if (productIds.length) {
      dispatch(settingsActions.GET_CUSTOMER_PLAN.REQUEST({ productIds }));
    }
  }, [products, currentPriceId, dispatch]);

  const premiumPriceIds = useMemo(
    () => resolvePremiumPriceIds(plans, products?.prezziPremium),
    [plans, products?.prezziPremium],
  );
  const premiumPlans = useMemo(
    () => sortPlansByInterval(filterPlansByTier(plans, true, premiumPriceIds)),
    [plans, premiumPriceIds],
  );
  const currentRawPlan = useMemo(
    () => findPlanByPriceId(plans, currentPriceId),
    [plans, currentPriceId],
  );
  const currentPlan = currentRawPlan ? parsePlanDisplay(currentRawPlan) : null;
  const intervalOptions = useMemo(
    () => buildIntervalOptions(premiumPlans, { currentPriceId }),
    [premiumPlans, currentPriceId],
  );
  const defaultIntervalType = useMemo(
    () =>
      currentPlan
        ? resolveSelectableIntervalType(currentPlan.intervalType, premiumPlans)
        : intervalOptions[0]?.intervalType ?? null,
    [currentPlan?.intervalType, premiumPlans, intervalOptions],
  );

  useEffect(() => {
    if (!intervalOptions.length) return;
    const stillValid = intervalOptions.some((o) => o.intervalType === selectedIntervalType);
    if (!selectedIntervalType || !stillValid) {
      setSelectedIntervalType(defaultIntervalType ?? intervalOptions[0].intervalType);
    }
  }, [intervalOptions, defaultIntervalType, selectedIntervalType]);

  const selectedOption =
    intervalOptions.find((o) => o.intervalType === selectedIntervalType) || intervalOptions[0];
  const monthlyOption = intervalOptions.find((o) => o.intervalType === '4');
  const lowestMonthly = useMemo(
    () =>
      intervalOptions.length
        ? Math.min(...intervalOptions.map((o) => o.display.price))
        : null,
    [intervalOptions],
  );
  const comparisonRows = useMemo(() => buildTierComparisonRows(), []);

  const blockReason = useMemo(() => {
    if (productsLoading || plansLoading) return null;
    if (isAlreadyPremium) return 'alreadyPremium';
    if (isCustomPlan) return 'customPlan';
    if (!stripeClientId) return 'billingSetup';
    if (sepaProcessing) return 'sepaProcessing';
    if (sepaFailed) return 'sepaFailed';
    if (unpaidInvoiceUrl) return 'unpaidInvoice';
    if (locked) return 'expired';
    if (!premiumPlans.length) return 'noPlans';
    return null;
  }, [
    productsLoading,
    plansLoading,
    isAlreadyPremium,
    isCustomPlan,
    stripeClientId,
    sepaProcessing,
    sepaFailed,
    unpaidInvoiceUrl,
    locked,
    premiumPlans.length,
  ]);

  const upgradeBlocked = Boolean(blockReason);
  const isLoading = productsLoading || plansLoading;

  const requestPlanChange = useCallback(
    (rawPlan) => {
      if (!rawPlan || upgradeBlocked || !stripeClientId) return;
      setConfirmPlan(parsePlanDisplay(rawPlan));
    },
    [upgradeBlocked, stripeClientId],
  );

  useEffect(() => {
    if (!confirmPlan?.id || !stripeClientId) {
      setPreview(null);
      setPreviewError(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    paymentsApi
      .post('/previewSubscriptionChange', { clientId: stripeClientId, priceId: confirmPlan.id })
      .then(({ data }) => {
        if (!cancelled) setPreview(data);
      })
      .catch(() => {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(t('settings.premium.previewError'));
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [confirmPlan, stripeClientId, t]);

  const handleConfirm = () => {
    if (!confirmPlan || !stripeClientId) return;
    dispatch(
      settingsActions.CREATE_SUBSCRIPTION.REQUEST(
        { clientId: stripeClientId, priceId: confirmPlan.id },
        () => {
          dispatch(settingsActions.GET_SUBSCRIPTION_STATUS.REQUEST({}));
          dispatch(settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST({}));
          setConfirmPlan(null);
          navigate(ROUTES.HOME);
        },
      ),
    );
  };

  const goToSupport = () => navigate(ROUTES.SUPPORT);
  const goToBilling = () => navigate(`${ROUTES.SETTINGS}${ROUTES.PAYMENTS}`);

  const primaryCta = (
    <button type="button" className={styles.cta} onClick={() => scrollTo('premium-offer')}>
      {upgradeBlocked ? t('settings.premium.ctaBlocked') : t('settings.premium.cta')}
      <ArrowRight size={17} strokeWidth={2.4} aria-hidden />
    </button>
  );

  const renderBlockedActions = () => {
    if (blockReason === 'alreadyPremium') {
      return (
        <button type="button" className={styles.cta} onClick={() => navigate(ROUTES.HOME)}>
          {t('settings.premium.blocked.goHome')}
          <ArrowRight size={16} strokeWidth={2.4} aria-hidden />
        </button>
      );
    }
    if (['unpaidInvoice', 'sepaFailed', 'expired', 'billingSetup'].includes(blockReason)) {
      return (
        <>
          <button type="button" className={styles.cta} onClick={goToBilling}>
            {t('settings.premium.blocked.goBilling')}
            <ArrowRight size={16} strokeWidth={2.4} aria-hidden />
          </button>
          <button type="button" className={styles.cta_ghost} onClick={goToSupport}>
            <Headphones size={16} strokeWidth={2.2} aria-hidden />
            {t('settings.premium.blocked.contactSupport')}
          </button>
        </>
      );
    }
    return (
      <button type="button" className={styles.cta} onClick={goToSupport}>
        <Headphones size={16} strokeWidth={2.2} aria-hidden />
        {t('settings.premium.blocked.contactSupport')}
      </button>
    );
  };

  const renderBilledLine = (option) => {
    const { display } = option;
    if (display.billingMonths <= 1) return t('settings.premium.pricing.billedMonthly');
    if (display.billingMonths >= 12) {
      return t('settings.premium.pricing.billedYearly', {
        amount: formatEuroAmount(display.totalAmount),
      });
    }
    return t('settings.premium.pricing.billedEvery', {
      amount: formatEuroAmount(display.totalAmount),
      months: display.billingMonths,
    });
  };

  const renderPricingPanel = () => {
    if (!selectedOption) return null;
    const monthlyBase = monthlyOption?.display.price;
    const showStrike =
      monthlyBase && selectedOption.display.price < monthlyBase - 0.005;
    const isCurrent = selectedOption.planId === currentPriceId;

    return (
      <div className={styles.pricing}>
        <div className={styles.pricing_top}>
          <span className={styles.pricing_tier}>
            <Flame size={16} strokeWidth={2.4} fill="currentColor" aria-hidden />
            Premium
          </span>
          {selectedOption.savingsPercent ? (
            <span className={styles.pricing_save}>
              {t('settings.premium.pricing.saveBadge', { percent: selectedOption.savingsPercent })}
            </span>
          ) : null}
        </div>

        <div
          className={styles.interval_toggle}
          role="radiogroup"
          aria-label={t('settings.payments.planCompare.chooseBilling')}
        >
          {intervalOptions.map((option) => {
            const active = option.intervalType === selectedOption.intervalType;
            return (
              <button
                key={option.planId}
                type="button"
                role="radio"
                aria-checked={active}
                className={clsx(styles.interval_btn, { [styles.interval_btn_active]: active })}
                onClick={() => setSelectedIntervalType(option.intervalType)}
              >
                {option.intervalLabel}
                {option.savingsPercent ? (
                  <em className={styles.interval_pct}>-{option.savingsPercent}%</em>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className={styles.price_block}>
          {showStrike ? (
            <s className={styles.price_strike}>{formatEuroAmount(monthlyBase)}€</s>
          ) : null}
          <div className={styles.price_main}>
            <span className={styles.price_value}>
              {formatEuroAmount(selectedOption.display.price)}€
            </span>
            <span className={styles.price_unit}>{t('settings.premium.pricing.perMonth')}</span>
          </div>
          <p className={styles.price_billed}>{renderBilledLine(selectedOption)}</p>
        </div>

        <button
          type="button"
          className={clsx(styles.cta, styles.cta_block)}
          disabled={isSubmitting || isCurrent}
          onClick={() => requestPlanChange(selectedOption.rawPlan)}
        >
          {isSubmitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : isCurrent ? (
            t('settings.payments.planCompare.currentPlanCta')
          ) : (
            <>
              {t('settings.premium.pricing.cta', { interval: selectedOption.intervalLabel })}
              <ArrowRight size={17} strokeWidth={2.4} aria-hidden />
            </>
          )}
        </button>

        <ul className={styles.pricing_bullets}>
          {[0, 1, 2].map((i) => (
            <li key={i}>
              <Check size={15} strokeWidth={2.6} aria-hidden />
              {t(`settings.premium.pricing.bullets.${i}`)}
            </li>
          ))}
        </ul>

        {currentPlan && !currentPlan.isPremium ? (
          <p className={styles.pricing_note}>{t('settings.premium.pricing.prorationNote')}</p>
        ) : null}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => navigate(-1)}>
        <ArrowLeft size={15} strokeWidth={2.4} aria-hidden />
        {t('settings.premium.back')}
      </button>

      {/* ——— HERO ——— */}
      <section className={styles.hero} aria-labelledby="premium-headline">
        <div className={styles.hero_media} aria-hidden />
        <div className={styles.hero_shade} aria-hidden />
        <div className={styles.hero_inner}>
          <p className={styles.brand}>{t('settings.premium.brand')}</p>
          <h1 id="premium-headline" className={styles.headline}>
            {t('settings.premium.headline')}
          </h1>
          <p className={styles.lead}>{t('settings.premium.lead')}</p>

          <div className={styles.cta_row}>
            {primaryCta}
            {!upgradeBlocked && lowestMonthly ? (
              <p className={styles.hero_price}>
                {t('settings.premium.heroPriceFrom', { price: formatEuroAmount(lowestMonthly) })}
              </p>
            ) : null}
          </div>

          <ul className={styles.trust}>
            {['instant', 'flexible', 'noSetup'].map((k) => (
              <li key={k}>
                <Check size={14} strokeWidth={2.8} aria-hidden />
                {t(`settings.premium.trust.${k}`)}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ——— BENEFICI ——— */}
      <section id="premium-features" className={styles.features} aria-labelledby="premium-features-title">
        <div className={styles.section_head}>
          <p className={styles.kicker}>{t('settings.premium.featuresKicker')}</p>
          <h2 id="premium-features-title" className={styles.section_title}>
            {t('settings.premium.featuresTitle')}
          </h2>
          <p className={styles.section_copy}>{t('settings.premium.featuresCopy')}</p>
        </div>
        <div className={styles.features_grid}>
          {FEATURES.map(({ key, Icon }, index) => (
            <article
              key={key}
              className={styles.feature}
              style={{ '--i': index }}
            >
              <span className={styles.feature_icon} aria-hidden>
                <Icon size={22} strokeWidth={2} />
              </span>
              <h3 className={styles.feature_title}>{t(`settings.premium.features.${key}.title`)}</h3>
              <p className={styles.feature_body}>{t(`settings.premium.features.${key}.body`)}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ——— OFFERTA ——— */}
      <section id="premium-offer" className={styles.offer} aria-labelledby="premium-offer-title">
        <div className={styles.section_head}>
          <p className={styles.kicker}>{t('settings.premium.pricingKicker')}</p>
          <h2 id="premium-offer-title" className={styles.section_title}>
            {upgradeBlocked
              ? t(`settings.premium.blocked.${blockReason}.title`)
              : t('settings.premium.pricingTitle')}
          </h2>
          {!upgradeBlocked ? (
            <p className={styles.section_copy}>{t('settings.premium.pricingCopy')}</p>
          ) : null}
        </div>

        {isLoading ? (
          <div className={styles.loading} role="status">
            <CircularProgress size={28} />
            <span>{t('settings.payments.loading')}</span>
          </div>
        ) : upgradeBlocked ? (
          <aside className={styles.blocked} role="status">
            <p className={styles.blocked_text}>
              {t(`settings.premium.blocked.${blockReason}.body`)}
            </p>
            <div className={styles.blocked_actions}>{renderBlockedActions()}</div>
          </aside>
        ) : (
          <div className={styles.offer_grid}>
            {renderPricingPanel()}

            <div className={styles.compare}>
              <div className={styles.compare_head}>
                <span className={styles.compare_feature_col}>
                  {t('settings.premium.compareTitle')}
                </span>
                <span className={styles.compare_col}>Basic</span>
                <span className={clsx(styles.compare_col, styles.compare_col_premium)}>
                  <Flame size={13} strokeWidth={2.4} fill="currentColor" aria-hidden />
                  Premium
                </span>
              </div>
              {comparisonRows.map((row) => (
                <div key={row.label} className={styles.compare_row}>
                  <span className={styles.compare_feature_col}>{row.label}</span>
                  <span className={styles.compare_col}>
                    {row.basic ? (
                      <Check size={16} strokeWidth={2.6} className={styles.tick_muted} aria-label="Basic: sì" />
                    ) : (
                      <Minus size={16} strokeWidth={2.2} className={styles.tick_none} aria-label="Basic: no" />
                    )}
                  </span>
                  <span className={clsx(styles.compare_col, styles.compare_col_premium)}>
                    {row.premium ? (
                      <Check size={16} strokeWidth={2.8} className={styles.tick_gold} aria-label="Premium: sì" />
                    ) : (
                      <Minus size={16} strokeWidth={2.2} className={styles.tick_none} aria-label="Premium: no" />
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {subscriptionError ? <p className={styles.error}>{subscriptionError}</p> : null}
      </section>

      {/* ——— FAQ ——— */}
      <section className={styles.faq} aria-labelledby="premium-faq-title">
        <div className={styles.section_head}>
          <p className={styles.kicker}>{t('settings.premium.faqKicker')}</p>
          <h2 id="premium-faq-title" className={styles.section_title}>
            {t('settings.premium.faqTitle')}
          </h2>
        </div>
        <div className={styles.faq_list}>
          {FAQ_KEYS.map((k) => (
            <details key={k} className={styles.faq_item}>
              <summary className={styles.faq_q}>
                {t(`settings.premium.faq.${k}.q`)}
                <ChevronDown size={18} strokeWidth={2.2} aria-hidden className={styles.faq_chevron} />
              </summary>
              <p className={styles.faq_a}>{t(`settings.premium.faq.${k}.a`)}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ——— CTA FINALE ——— */}
      {!upgradeBlocked ? (
        <section className={styles.final}>
          <div className={styles.final_inner}>
            <div>
              <h2 className={styles.final_title}>{t('settings.premium.finalTitle')}</h2>
              <p className={styles.final_copy}>{t('settings.premium.finalCopy')}</p>
            </div>
            <div className={styles.final_actions}>
              {primaryCta}
              <button type="button" className={styles.cta_ghost} onClick={goToSupport}>
                <Headphones size={16} strokeWidth={2.2} aria-hidden />
                {t('settings.premium.blocked.contactSupport')}
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <Dialog
        open={!!confirmPlan}
        onClose={() => setConfirmPlan(null)}
        maxWidth="xs"
        fullWidth
        classes={{ paper: planStyles.confirm_paper }}
      >
        <DialogTitle className={planStyles.confirm_title}>
          {t('settings.premium.confirmTitle')}
        </DialogTitle>
        <DialogContent className={planStyles.confirm_dialog}>
          {confirmPlan ? (
            <>
              <div className={clsx(planStyles.confirm_plan_chip, planStyles.confirm_plan_chip_premium)}>
                <span className={planStyles.confirm_plan_chip_icon} aria-hidden>
                  <Flame size={18} strokeWidth={2.2} fill="currentColor" />
                </span>
                <div className={planStyles.confirm_plan_chip_copy}>
                  <strong>Premium · {confirmPlan.intervalLabel}</strong>
                  <span>
                    {t('settings.premium.confirmMessage', {
                      interval: confirmPlan.intervalLabel,
                      price: formatEuroAmount(confirmPlan.price),
                    })}
                  </span>
                </div>
              </div>
              {previewLoading ? (
                <div className={planStyles.preview_box}>
                  <CircularProgress size={22} />
                  <span>{t('settings.premium.previewLoading')}</span>
                </div>
              ) : null}
              {previewError ? <p className={styles.error}>{previewError}</p> : null}
              {preview ? (
                <div className={clsx(planStyles.preview_box, planStyles.preview_box_premium)}>
                  <p className={planStyles.preview_box_label}>{t('settings.premium.previewLabel')}</p>
                  {preview.hasImmediateCharge ? (
                    <p className={planStyles.preview_row}>
                      <span>{t('settings.premium.chargeToday')}</span>
                      <strong>
                        {formatEuroAmount(preview.amountDue, { fromCents: true })} € + IVA
                      </strong>
                    </p>
                  ) : (
                    <p className={planStyles.preview_note}>{t('settings.premium.noImmediateCharge')}</p>
                  )}
                  {(preview.recurringMonthlyPrice ?? confirmPlan.price) != null && (
                    <p className={planStyles.preview_row}>
                      <span>{t('settings.premium.nextRenewal')}</span>
                      <strong>
                        {formatEuroAmount(preview.recurringMonthlyPrice ?? confirmPlan.price)} € + IVA / mese
                      </strong>
                    </p>
                  )}
                </div>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions className={planStyles.confirm_actions}>
          <button
            type="button"
            className={planStyles.confirm_btn_secondary}
            onClick={() => setConfirmPlan(null)}
          >
            {t('common.actions.cancel')}
          </button>
          <button
            type="button"
            className={clsx(planStyles.confirm_btn_primary, planStyles.confirm_btn_primary_premium)}
            onClick={handleConfirm}
            disabled={isSubmitting || previewLoading}
          >
            {isSubmitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t('settings.premium.confirmCta')
            )}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PremiumUpgrade;
